import { ScanLibraryRule, ScanProvider } from '../../shared/@types/document-scanning.type';
import { API_URLS } from '../../api';
import type {
  IClientSecuritySettings,
  IClientSecuritySettingsUpdate,
  IRemoveUserClientResult,
  ISessionExemption,
  IUserBlockState,
  IUserClients,
} from '../../shared/@types/security-settings.type';
import axios from '../../utils/axios';
import { IClient, IUser } from '../../redux/clients/clients.type';
import { IMappedList } from '../../pages/dashboard/stakeholderMatching/SubmittedMatchingList/SubmittedMatchingList';
import { IReloadServiceValues } from '../../redux/monitoring/monitoring';
import {
  ISupplierPullQuery,
  ITransactionsQuery,
  IUsageQuery,
} from '../../shared/@types/usage.type';
import type {
  ITxApiClientOverview,
  ITxApiCredential,
  ITxApiLogPage,
  ITxApiLogQuery,
  ITxApiLogSummary,
  ITxApiMintedCredential,
  ITxApiPolicyResponse,
  ITxApiRegistrationPage,
  TxApiRegistrationType,
  ITxApiResponse,
  ITxApiScreeningPolicy,
  ITxApiSetupStatus,
  ITxApiSigningExample,
  ITxApiMintedWebhook,
  ITxApiWebhookEndpoint,
  ITxApiWebhookMint,
  ITxApiWebhookPatch,
  TxApiEnvironment,
} from '../../shared/@types/transactionsApi.type';

export enum Method {
  GET = 'GET',
  POST = 'POST',
  DELETE = 'DELETE',
  PUT = 'PUT',
}

export enum Entry {
  Alerts = 'alerts',
  Cases = 'cases',
}

export default class RestService {
  public static login(
    email: string,
    password: string,
    otpCode?: string,
    useRecoveryCode?: boolean
  ) {
    const otpCredential = otpCode
      ? useRecoveryCode
        ? { recoveryCode: otpCode }
        : { otpCode }
      : {};
    return axios.post(API_URLS.LOGIN, {
      email,
      password,
      ...otpCredential,
      // for BE checking role
      main: 'no',
    });
  }

  // ===== Client security settings (user-management /security-settings, KYP only) =====
  public static getClientsSecuritySettings() {
    return axios.get<{ data: IClientSecuritySettings[] }>(API_URLS.SECURITY_SETTINGS_CLIENTS);
  }

  public static setClientSecuritySettings(
    clientId: string,
    settings: IClientSecuritySettingsUpdate
  ) {
    return axios.put<{ data: IClientSecuritySettings }>(
      API_URLS.SECURITY_SETTINGS_CLIENT(clientId),
      settings
    );
  }

  /** Suspends (nobody of theirs can sign in) or reinstates a client; KYP only. */
  public static setClientSuspension(clientId: string, suspended: boolean, reason = '') {
    return axios.put<{ data: IClientSecuritySettings }>(API_URLS.CLIENT_SUSPENSION(clientId), {
      suspended,
      reason,
    });
  }

  /** The clients a user belongs to (multi-client access); KYP only. */
  public static getUserClients(userId: string) {
    return axios.get<{ data: IUserClients }>(API_URLS.USER_CLIENTS(userId));
  }

  /** Adds a client to a user, or sets their roles at it (empty = default roles). */
  public static setUserClient(userId: string, clientId: string, roles: string[] = []) {
    return axios.put<{ data: IUserClients }>(API_URLS.USER_CLIENT(userId, clientId), { roles });
  }

  /** Removes a client from a user; with deleteIfLast, deletes the account when it was their last. */
  public static removeUserClient(userId: string, clientId: string, deleteIfLast = false) {
    return axios.delete<{ data: IRemoveUserClientResult }>(API_URLS.USER_CLIENT(userId, clientId), {
      params: deleteIfLast ? { deleteIfLast: true } : undefined,
    });
  }

  /** Blocks or unblocks one account; KYP only. */
  public static setUserBlock(userId: string, blocked: boolean, reason = '') {
    return axios.put<{ data: IUserBlockState }>(API_URLS.USER_BLOCK(userId), { blocked, reason });
  }

  /** Accounts exempt from the session idle timeout (KYP only). */
  public static getSessionExemptions() {
    return axios.get<{ data: ISessionExemption[] }>(API_URLS.SESSION_EXEMPTIONS);
  }

  public static addSessionExemption(email: string) {
    return axios.post<{ data: ISessionExemption }>(API_URLS.SESSION_EXEMPTIONS, { email });
  }

  public static removeSessionExemption(userId: string) {
    return axios.delete(API_URLS.SESSION_EXEMPTION(userId));
  }

  /** Tells user-management the user is active (idle timeout); see IdleSessionGuard. */
  public static sessionActivity(refresh: string) {
    return axios.post(API_URLS.SESSION_ACTIVITY, { refresh });
  }

  public static logout(refreshToken: string) {
    return axios.post(API_URLS.LOGOUT, {
      refresh: refreshToken,
    });
  }

  public static refreshToken(token: string) {
    return axios.post(API_URLS.REFRESH_TOKEN, { refresh: token });
  }

  public static getClients() {
    return axios.get(API_URLS.GET_CLIENTS);
  }

  public static getClientsFeatures() {
    return axios.get(API_URLS.GET_CLIENTS_FEATURES);
  }

  // ===== Transactions API setup (server/txapiadmin; KYP only) =====
  public static getTxApiClients() {
    return axios.get<ITxApiResponse<ITxApiClientOverview[]>>(API_URLS.TX_API_CLIENTS);
  }

  public static getTxApiSetup(clientId: string) {
    return axios.get<ITxApiResponse<ITxApiSetupStatus>>(API_URLS.TX_API_CLIENT(clientId));
  }

  public static getTxApiKeys(clientId: string) {
    return axios.get<ITxApiResponse<ITxApiCredential[]>>(API_URLS.TX_API_KEYS(clientId));
  }

  /** Mints a key id + secret; the secret is in this response ONLY. */
  public static mintTxApiKey(clientId: string, label: string, environment: TxApiEnvironment) {
    return axios.post<ITxApiResponse<ITxApiMintedCredential>>(API_URLS.TX_API_KEYS(clientId), {
      label,
      environment,
    });
  }

  public static updateTxApiKey(
    clientId: string,
    keyId: string,
    patch: { label?: string; disabled?: boolean }
  ) {
    return axios.put<ITxApiResponse<ITxApiCredential>>(API_URLS.TX_API_KEY(clientId, keyId), patch);
  }

  /** New secret for the same key id; the old secret stops working at once. */
  public static rotateTxApiKey(clientId: string, keyId: string) {
    return axios.post<ITxApiResponse<ITxApiMintedCredential>>(
      API_URLS.TX_API_KEY_ROTATE(clientId, keyId)
    );
  }

  public static deleteTxApiKey(clientId: string, keyId: string) {
    return axios.delete<ITxApiResponse<{ keyId: string; status: string }>>(
      API_URLS.TX_API_KEY(clientId, keyId)
    );
  }

  // Provider push door: webhook endpoints per client.
  public static getTxApiWebhookProviders() {
    return axios.get<ITxApiResponse<string[]>>(API_URLS.TX_API_WEBHOOK_PROVIDERS);
  }

  public static getTxApiWebhooks(clientId: string) {
    return axios.get<ITxApiResponse<ITxApiWebhookEndpoint[]>>(API_URLS.TX_API_WEBHOOKS(clientId));
  }

  public static mintTxApiWebhook(clientId: string, body: ITxApiWebhookMint) {
    return axios.post<ITxApiResponse<ITxApiMintedWebhook>>(API_URLS.TX_API_WEBHOOKS(clientId), body);
  }

  public static updateTxApiWebhook(clientId: string, webhookId: string, patch: ITxApiWebhookPatch) {
    return axios.put<ITxApiResponse<ITxApiWebhookEndpoint>>(
      API_URLS.TX_API_WEBHOOK(clientId, webhookId),
      patch
    );
  }

  public static rotateTxApiWebhook(clientId: string, webhookId: string) {
    return axios.post<ITxApiResponse<ITxApiMintedWebhook>>(
      API_URLS.TX_API_WEBHOOK_ROTATE(clientId, webhookId)
    );
  }

  public static deleteTxApiWebhook(clientId: string, webhookId: string) {
    return axios.delete<ITxApiResponse<{ webhookId: string; status: string }>>(
      API_URLS.TX_API_WEBHOOK(clientId, webhookId)
    );
  }

  public static getTxApiPolicy(clientId: string) {
    return axios.get<ITxApiResponse<ITxApiPolicyResponse>>(API_URLS.TX_API_POLICY(clientId));
  }

  public static saveTxApiPolicy(clientId: string, policy: ITxApiScreeningPolicy) {
    return axios.put<ITxApiResponse<ITxApiScreeningPolicy>>(
      API_URLS.TX_API_POLICY(clientId),
      policy
    );
  }

  public static deleteTxApiPolicy(clientId: string) {
    return axios.delete<ITxApiResponse<{ status: string }>>(API_URLS.TX_API_POLICY(clientId));
  }

  public static getTxApiRegistrations(
    clientId: string,
    params: { type: TxApiRegistrationType; search?: string; page?: number; size?: number }
  ) {
    return axios.get<ITxApiResponse<ITxApiRegistrationPage>>(
      API_URLS.TX_API_REGISTRATIONS(clientId),
      { params }
    );
  }

  public static getTxApiLog(clientId: string, params: ITxApiLogQuery) {
    return axios.get<ITxApiResponse<ITxApiLogPage>>(API_URLS.TX_API_LOG(clientId), { params });
  }

  public static getTxApiLogSummary(clientId: string, params: { from?: string; to?: string }) {
    return axios.get<ITxApiResponse<ITxApiLogSummary>>(API_URLS.TX_API_LOG_SUMMARY(clientId), {
      params,
    });
  }

  public static getTxApiSigningExample() {
    return axios.get<ITxApiResponse<ITxApiSigningExample>>(API_URLS.TX_API_SIGNING_EXAMPLE);
  }

  /** Supervisors + their mapped tenants (names from client documents). */
  public static getSupervisors() {
    return axios.get(API_URLS.GET_SUPERVISORS);
  }

  public static setSupervisorFlag(clientId: string, isSupervisor: boolean) {
    return axios.put(API_URLS.SET_SUPERVISOR_FLAG(clientId), { isSupervisor });
  }

  /** Link a tenant to a supervisor (tenant-keyed mapping). */
  public static linkSupervisedTenant(supervisorId: string, tenantClientId: string) {
    return axios.put(API_URLS.UPSERT_SUPERVISOR_MAPPING(supervisorId), { tenantClientId });
  }

  public static unlinkSupervisedTenant(supervisorId: string, tenantClientId: string) {
    return axios.delete(API_URLS.DELETE_SUPERVISOR_MAPPING(supervisorId, tenantClientId));
  }

  public static setClientFeatures(clientId: string, features: Record<string, boolean>) {
    return axios.put(API_URLS.SET_CLIENT_FEATURES(clientId), { features });
  }

  // ===== Sender domains (onboarding module; /admin/onboarding/domains) =====
  public static getOnboardingDomains() {
    return axios.get(API_URLS.GET_ONBOARDING_DOMAINS);
  }

  public static addOnboardingDomain(clientID: string, domain: string) {
    return axios.post(API_URLS.ADD_ONBOARDING_DOMAIN, { clientID, domain });
  }

  public static deleteOnboardingDomain(domain: string) {
    return axios.delete(API_URLS.DELETE_ONBOARDING_DOMAIN(domain));
  }

  public static getDocumentScanningLibrary() {
    return axios.get(API_URLS.DOCUMENT_SCANNING_LIBRARY);
  }

  public static getDocumentScanningProvider() {
    return axios.get(API_URLS.DOCUMENT_SCANNING_PROVIDER);
  }

  public static saveDocumentScanningProvider(provider: ScanProvider) {
    return axios.put(API_URLS.DOCUMENT_SCANNING_PROVIDER, { provider });
  }

  public static renameDocumentScanningType(id: string, name: string) {
    return axios.put(API_URLS.DOCUMENT_SCANNING_TYPE(id), { name });
  }

  public static saveDocumentScanningRule(check: ScanLibraryRule, isNew: boolean, newDocumentTypeName?: string) {
    const payload = { check, ...(isNew ? { runningOrder: check.runningOrder, ...(newDocumentTypeName ? { newDocumentTypeName } : {}) } : {}) };
    return isNew ? axios.post(API_URLS.DOCUMENT_SCANNING_CHECKS, payload) : axios.put(API_URLS.DOCUMENT_SCANNING_CHECK(check.id), payload);
  }

  public static archiveDocumentScanningRule(id: string) {
    return axios.post(API_URLS.DOCUMENT_SCANNING_ARCHIVE(id));
  }

  public static restoreDocumentScanningRule(id: string) {
    return axios.post(API_URLS.DOCUMENT_SCANNING_RESTORE(id));
  }

  // period is YYYY-MM. Omitted means the current billing period, which is what
  // this endpoint did unconditionally before it learned about history.
  public static getClientsCommitments(period?: string) {
    return axios.get(API_URLS.GET_CLIENTS_COMMITMENTS, {
      params: period ? { period } : undefined,
    });
  }

  // Levers omitted from minimumCommitments are cleared server-side — that is
  // how a client with no commitment for a lever is expressed.
  public static setClientCommitments(
    clientId: string,
    minimumCommitments: Record<string, { monthlyCap: number }>
  ) {
    return axios.put(API_URLS.SET_CLIENT_COMMITMENTS(clientId), { minimumCommitments });
  }

  public static inviteUser(client: IUser, clientId: string) {
    return axios.post(API_URLS.INVITE_USER, { ...client, clientID: clientId });
  }

  public static createClient(client: Partial<IClient>) {
    return axios.post(API_URLS.CREATE_CLIENT, client);
  }

  public static getClientById(clientId: string) {
    return axios.get(API_URLS.GET_CLIENT_BY_ID(clientId));
  }

  public static getUsers() {
    return axios.get(API_URLS.GET_USERS);
  }

  /** Deletes a platform user (KYP only); also drops them from teams and ends their sessions. */
  public static deleteUser(userId: string) {
    return axios.delete(API_URLS.DELETE_USER(userId));
  }

  /** Edits a platform user (KYP only); only the fields sent change. */
  public static updateUser(
    userId: string,
    changes: { fName?: string; lName?: string; email?: string; roles?: string[] }
  ) {
    return axios.put(API_URLS.UPDATE_USER(userId), changes);
  }

  public static getMatches(companyId: string) {
    return axios.get(API_URLS.GET_MATCHES(companyId));
  }

  public static updateMatches(list: { reviews: Partial<IMappedList>[] }) {
    return axios.put(API_URLS.UPDATE_MATCHES, list);
  }

  public static getCompanies(companyId?: string) {
    const config = companyId && companyId?.length ? { params: { filter: companyId } } : {};
    return axios.get(API_URLS.GET_COMPANIES, config);
  }

  public static getRelatedInfoLogs() {
    return axios.get(API_URLS.GET_RELATED_INFO_LOGS);
  }

  public static getClientsStatistic() {
    return axios.get(API_URLS.GET_CLIENTS_STATISTIC);
  }

  public static getClientsCompanies() {
    return axios.get(API_URLS.GET_CLIENTS_COMPANIES);
  }

  public static getCompaniesList() {
    return axios.get(API_URLS.GET_ALL_COMPANIES_LIST);
  }

  public static reloadServices(data: { updateAPIs: IReloadServiceValues }, companyId: string) {
    return axios.put(API_URLS.RELOAD_SERVICES(companyId), data);
  }

  public static downloadPdf(clientId: string, companyId: string) {
    return axios.get(API_URLS.DOWNLOAD_PDF(clientId, companyId), { responseType: 'blob' });
  }

  // ---------------------------------------------------------------- usage
  // Platform usage monitoring. Every endpoint takes the same query envelope,
  // so callers vary only the fields they care about. Undefined fields are
  // dropped by axios rather than sent as "undefined", which matters because the
  // backend treats an empty filter as "no filter" and a present-but-empty one
  // the same way — but a literal "undefined" would filter to nothing.

  public static getUsageCatalogue() {
    return axios.get(API_URLS.GET_USAGE_CATALOGUE);
  }

  public static getUsageSummary(params: IUsageQuery) {
    return axios.get(API_URLS.GET_USAGE_SUMMARY, { params });
  }

  public static getUsageTimeseries(params: IUsageQuery) {
    return axios.get(API_URLS.GET_USAGE_TIMESERIES, { params });
  }

  public static getUsageBreakdown(params: IUsageQuery) {
    return axios.get(API_URLS.GET_USAGE_BREAKDOWN, { params });
  }

  public static getUsageClients(params: IUsageQuery) {
    return axios.get(API_URLS.GET_USAGE_CLIENTS, { params });
  }

  public static getUsageEvents(params: IUsageQuery) {
    return axios.get(API_URLS.GET_USAGE_EVENTS, { params });
  }

  public static getUsageHealth() {
    return axios.get(API_URLS.GET_USAGE_HEALTH);
  }

  // Portfolios for the usage filter. Pass a clientID to cascade the picker off
  // the client filter; omit it for every portfolio across every client.
  public static getUsagePortfolios(clientID?: string) {
    return axios.get(API_URLS.GET_USAGE_PORTFOLIOS, {
      params: clientID ? { clientID } : undefined,
    });
  }

  // Monitored domains right now, per client. No window — it is a standing count.
  public static getUsageDomains(params: { clientID?: string; portfolioID?: string }) {
    return axios.get(API_URLS.GET_USAGE_DOMAINS, { params });
  }

  // Transaction-monitoring volume (ClientData.CompanyHourlyMetrics).
  public static getUsageTransactions(params: ITransactionsQuery) {
    return axios.get(API_URLS.GET_USAGE_TRANSACTIONS, { params });
  }

  // Supplier-side cost (Monitoring.SupplierPulls), not client usage.
  public static getSupplierPulls(params: ISupplierPullQuery) {
    return axios.get(API_URLS.GET_SUPPLIER_PULLS, { params });
  }

  public static downloadUsageExport(params: IUsageQuery) {
    return axios.get(API_URLS.GET_USAGE_EXPORT, { params, responseType: 'blob' });
  }
}
